import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Trophy, Share2, RefreshCw, ArrowLeft, Crown, Target } from 'lucide-react';
import LoopingAdBanner from '../components/LoopingAdBanner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace('http://', 'https://');
const POLL_MS = 5000;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3ec18717-9700-48f1-a5a2-71ac0edd3a6f/artifacts/29waugc4_WhatsApp%20Image%202026-03-06%20at%2016.26.44.jpeg';

export default function WatchMatch() {
  const { shortId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);

  const fetchState = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/live-watch/${shortId}`);
      if (res.status === 404) {
        setError('Session expired or not found.');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setState(data);
      setLastSync(new Date());
      setError(null);
    } catch (e) {
      setError(e.message || 'Network error');
    }
  }, [shortId]);

  useEffect(() => {
    fetchState();
    const id = setInterval(fetchState, POLL_MS);
    return () => clearInterval(id);
  }, [fetchState]);

  const handleShare = () => {
    const text = state
      ? `🏸 Live: ${state.side_a} vs ${state.side_b}\nWatch on LoveAll: ${window.location.href}`
      : `Watch this badminton match live on LoveAll: ${window.location.href}`;
    if (navigator.share) {
      navigator.share({ title: 'LoveAll Live', text }).catch((err) => {
        if (process.env.NODE_ENV !== 'production') console.debug('share cancelled:', err?.message);
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="watch-error">
        <Card className="max-w-sm w-full">
          <CardContent className="p-8 text-center space-y-3">
            <Trophy className="h-10 w-10 mx-auto text-muted-foreground" />
            <h2 className="font-heading text-lg font-bold uppercase">Match Unavailable</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to LoveAll
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="watch-loading">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
          <p className="text-sm text-muted-foreground">Loading live score…</p>
        </div>
      </div>
    );
  }

  const currentGame = state.games?.[state.current_game_idx] || { scoreA: 0, scoreB: 0 };
  const winnerName = state.winner === 'A' ? state.side_a : state.side_b;
  const gamesWonA = (state.games || []).filter(g => g.winner === 'A').length;
  const gamesWonB = (state.games || []).filter(g => g.winner === 'B').length;
  const isLive = !state.completed_at;

  return (
    <div className="min-h-screen bg-background safe-top safe-bottom" data-testid="watch-page">
      {/* Header */}
      <div className="bg-card/95 border-b border-border px-4 py-3 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="LoveAll" className="h-8 w-8 rounded-full object-cover" />
            <div>
              <p className="font-heading text-sm font-extrabold uppercase tracking-tight leading-none">LoveAll Live</p>
              <p className="text-[10px] text-muted-foreground">
                <RefreshCw className="h-2.5 w-2.5 inline mr-0.5" />
                Auto-refreshes every {POLL_MS / 1000}s
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isLive ? (
              <Badge className="bg-red-500/15 text-red-500 border-red-500/30 text-[10px]">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse mr-1" /> LIVE
              </Badge>
            ) : (
              <Badge className="bg-secondary/15 text-secondary border-secondary/30 text-[10px]">FINAL</Badge>
            )}
            <Button onClick={handleShare} size="sm" variant="ghost" className="h-9 px-2" data-testid="watch-share-btn">
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Match meta */}
        <div className="text-center space-y-1">
          <Badge variant="outline" className="text-[10px]">{state.preset_name}</Badge>
          <h1 className="font-heading text-xl sm:text-2xl font-extrabold uppercase tracking-tight">
            {state.side_a} <span className="text-muted-foreground mx-2">vs</span> {state.side_b}
          </h1>
          <p className="text-[10px] text-muted-foreground">
            Set {(state.current_game_idx || 0) + 1} of {state.best_of}
          </p>
        </div>

        {/* Score */}
        <Card className="overflow-hidden border-2 border-primary/20">
          <div className="bg-slate-900 p-6 sm:p-10">
            {/* Game tally */}
            {state.best_of > 1 && (
              <div className="flex justify-center gap-8 mb-6">
                <div className="text-center">
                  <p className="text-slate-400 text-[10px] uppercase mb-1">Games</p>
                  <div className="flex gap-1.5">
                    {Array.from({ length: Math.ceil(state.best_of / 2) + 1 }).map((_, i) => (
                      <div key={`wA-${i}`} className={`w-2.5 h-2.5 rounded-full ${i < gamesWonA ? 'bg-primary' : 'bg-slate-700'}`} />
                    ))}
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-slate-400 text-[10px] uppercase mb-1">Games</p>
                  <div className="flex gap-1.5">
                    {Array.from({ length: Math.ceil(state.best_of / 2) + 1 }).map((_, i) => (
                      <div key={`wB-${i}`} className={`w-2.5 h-2.5 rounded-full ${i < gamesWonB ? 'bg-secondary' : 'bg-slate-700'}`} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 items-center gap-3">
              <div className="text-center">
                <p className="text-white font-heading text-sm sm:text-lg font-bold uppercase truncate">{state.side_a}</p>
                <p className="font-heading text-7xl sm:text-8xl font-black text-primary tabular-nums mt-2" data-testid="watch-score-a">{currentGame.scoreA}</p>
              </div>
              <div className="text-slate-500 text-3xl text-center">–</div>
              <div className="text-center">
                <p className="text-white font-heading text-sm sm:text-lg font-bold uppercase truncate">{state.side_b}</p>
                <p className="font-heading text-7xl sm:text-8xl font-black text-secondary tabular-nums mt-2" data-testid="watch-score-b">{currentGame.scoreB}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Game history */}
        {state.games?.length > 1 && (
          <div className="flex justify-center gap-2 flex-wrap">
            {state.games.map((g, i) => (
              <Badge key={`gh-${i}`} variant="outline" className="font-mono text-[11px]">
                S{i + 1}: {g.scoreA}-{g.scoreB}{g.winner ? ` ✓` : ''}
              </Badge>
            ))}
          </div>
        )}

        {/* Final winner */}
        {state.winner && (
          <Card className="border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-orange-500/10">
            <CardContent className="p-6 text-center">
              <Crown className="h-10 w-10 mx-auto text-amber-500 mb-2" />
              <p className="text-[10px] uppercase tracking-widest text-amber-500 font-bold">Champion</p>
              <p className="font-heading text-2xl font-extrabold uppercase tracking-tight mt-1">{winnerName}</p>
            </CardContent>
          </Card>
        )}

        {/* Last sync */}
        {lastSync && (
          <p className="text-center text-[10px] text-muted-foreground">
            Last update: {lastSync.toLocaleTimeString()}
          </p>
        )}

        {/* Convert viewer → player CTA */}
        <Card className="border-2 border-secondary/40 bg-gradient-to-br from-secondary/10 to-primary/10">
          <CardContent className="p-5 text-center space-y-3">
            <p className="text-[10px] uppercase tracking-widest text-secondary font-bold">Inspired?</p>
            <h3 className="font-heading text-xl font-extrabold uppercase tracking-tight">Start Your Own Match</h3>
            <p className="text-xs text-muted-foreground">
              No sign-up. Score live, track streaks, share with friends.
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/')}
              className="w-full h-14 bg-secondary hover:bg-secondary/90 text-secondary-foreground font-heading font-extrabold uppercase tracking-wide text-base"
              data-testid="watch-start-own-match"
            >
              <Target className="mr-2 h-5 w-5" /> Open Quick Play
            </Button>
          </CardContent>
        </Card>

        {/* Ad banner */}
        <LoopingAdBanner variant="inline" />
      </div>
    </div>
  );
}
